import React from 'react';
import { Checkbox, Modal, PasswordInput, TextInput } from '@mantine/core';
import { Button } from '../../../components/Button';
import type { TenantView } from '../../../services/tenants/tenants.service';
import classes from '../tenants-page.module.scss';

const noSelectionProps = {
  onContextMenu: (event: React.MouseEvent<HTMLInputElement>) => event.preventDefault(),
  onSelect: (event: React.SyntheticEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const end = input.value.length;
    if (input.selectionStart !== end || input.selectionEnd !== end) {
      input.setSelectionRange(end, end);
    }
  },
};

type InitialShiftManagerModalProps = {
  tenant: TenantView | null;
  username: string;
  password: string;
  confirmPassword: string;
  permissions: {
    canEditEmployees: boolean;
    canAssignEmployees: boolean;
    canManageSchedule: boolean;
    canCreateEmployees: boolean;
  };
  isSaving: boolean;
  onUsernameChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  onPermissionChange: (
    permission: 'canEditEmployees' | 'canAssignEmployees' | 'canManageSchedule' | 'canCreateEmployees',
    checked: boolean,
  ) => void;
  onClose: () => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
};

export const InitialShiftManagerModal: React.FC<InitialShiftManagerModalProps> = ({
  tenant,
  username,
  password,
  confirmPassword,
  permissions,
  isSaving,
  onUsernameChange,
  onPasswordChange,
  onConfirmPasswordChange,
  onPermissionChange,
  onClose,
  onSubmit,
}) => (
  <Modal
    opened={Boolean(tenant)}
    onClose={onClose}
    title="יצירת מנהל SHIFT ראשוני"
    centered
    radius="md"
    closeOnClickOutside={false}
  >
    <form onSubmit={onSubmit} noValidate dir="rtl">
      <p>חברה: <strong>{tenant?.companyName}</strong></p>
      <TextInput
        label="שם משתמש"
        value={username}
        onChange={(event) => onUsernameChange(event.currentTarget.value)}
        required
        maxLength={50}
        disabled={isSaving}
        data-testid="shift-manager-username"
        {...noSelectionProps}
      />
      <PasswordInput
        label="סיסמה"
        value={password}
        onChange={(event) => onPasswordChange(event.currentTarget.value)}
        required
        disabled={isSaving}
        data-testid="shift-manager-password"
        {...noSelectionProps}
      />
      <PasswordInput
        label="אימות סיסמה"
        value={confirmPassword}
        onChange={(event) => onConfirmPasswordChange(event.currentTarget.value)}
        required
        disabled={isSaving}
        data-testid="shift-manager-confirm-password"
        {...noSelectionProps}
      />
      <fieldset>
        <legend>הרשאות מנהל</legend>
        <Checkbox
          label="שינוי פרטי עובדים"
          checked={permissions.canEditEmployees}
          onChange={(event) => onPermissionChange('canEditEmployees', event.currentTarget.checked)}
          disabled={isSaving}
          data-testid="shift-manager-can-edit-employees"
        />
        <Checkbox
          label="שיבוץ עובדים"
          checked={permissions.canAssignEmployees}
          onChange={(event) => onPermissionChange('canAssignEmployees', event.currentTarget.checked)}
          disabled={isSaving}
          data-testid="shift-manager-can-assign-employees"
        />
        <Checkbox
          label="ניהול פאנל משמרות"
          checked={permissions.canManageSchedule}
          onChange={(event) => onPermissionChange('canManageSchedule', event.currentTarget.checked)}
          disabled={isSaving}
          data-testid="shift-manager-can-manage-schedule"
        />
        <Checkbox
          label="יצירת עובדים"
          checked={permissions.canCreateEmployees}
          onChange={(event) => onPermissionChange('canCreateEmployees', event.currentTarget.checked)}
          disabled={isSaving}
          data-testid="shift-manager-can-create-employees"
        />
      </fieldset>
      <div className={classes.modalActions}>
        <Button variant="cancel" onClick={onClose} disabled={isSaving}>ביטול</Button>
        <Button
          type="submit"
          variant="save"
          loading={isSaving}
          loadingLabel="מפעיל..."
          dataTestId="shift-manager-submit"
        >
          צור והפעל SHIFT
        </Button>
      </div>
    </form>
  </Modal>
);